/**
 * O instante da assinatura pelo painel (E203.F4, ADR-0146 §6): a data e o horário com o fuso, sem
 * reenviar o protocolo inteiro, com fundamento; o caso é conferido de novo. Com peça concluída, o
 * servidor recusa (409) e o painel diz o caminho: reabrir a conclusão. E203.F4.C: a revisão leva a
 * versão do documento que o painel mostrava (`gravarRevisao`).
 */
import { useState } from 'react';

import { Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { ExameEletronicoError } from '@/lib/exame_eletronico_erros';
import { CHAVE_DO_INSTANTE } from '@/lib/revisaoDoDado';

import { Fundamento, fundamentoValido } from './Fundamento';
import { BotaoSalvar, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.instante;
const FUSO = /^[+-]\d{2}:\d{2}$/;

export function InstanteNoPainel(props: PropsDoConteudo) {
  const { acoes, onMudou } = props;
  const [local, setLocal] = useState('');
  const [fuso, setFuso] = useState('-03:00');
  const [fundamento, setFundamento] = useState('');
  const valido = local !== '' && FUSO.test(fuso) && fundamentoValido(fundamento);
  const gravacao = useGravacao(
    async () => {
      const comSegundos = local.length === 16 ? `${local}:00` : local;
      try {
        return await gravarRevisao(props, {
          alvo: 'instante',
          chave: CHAVE_DO_INSTANTE,
          valor: `${comSegundos}${fuso}`,
          fundamento: fundamento.trim(),
        });
      } catch (err) {
        if (err instanceof ExameEletronicoError && err.reason === 'concluido') {
          return ptBR.painelDoDado.protocolo.concluido;
        }
        throw err;
      }
    },
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  return (
    <div className="space-y-sc-3" data-testid="painel-instante">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      <Field label={t.rotulo} htmlFor="painel-instante-local">
        <Input
          id="painel-instante-local"
          type="datetime-local"
          step={1}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          data-testid="painel-instante-local"
        />
      </Field>
      <Field label={t.fuso} htmlFor="painel-instante-fuso">
        <Input
          id="painel-instante-fuso"
          value={fuso}
          onChange={(e) => setFuso(e.target.value)}
          data-testid="painel-instante-fuso"
        />
      </Field>
      <Fundamento id="painel-instante-fundamento" valor={fundamento} onChange={setFundamento} />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        desligado={!valido}
        onSalvar={() => void gravacao.salvar()}
        testId="painel-instante-salvar"
      />
    </div>
  );
}
